import type { GenerateCompositionPlanResponse } from "./generateCompositionPlan";
import { sanitizeLyricLines } from "./lyricsSanitize";
import { generateMusicViaDocEditor } from "./musicDocEditor";
import type { StemsZipRef } from "./jobResultBlob";
import type { MusicEditorRestoreState } from "./musicCustomData";

export interface GenerateMusicLyricsSection {
  section_name?: string;
  lines?: string[];
  duration_ms?: number;
}

export interface GenerateMusicTimestamp {
  text: string;
  startMs: number;
  endMs: number;
}

export type UploadStatus = "idle" | "uploading" | "uploaded" | "upload_failed";

export interface GenerateMusicResult {
  audioBlobUrl: string;
  audioBlob: Blob;
  filename: string;
  audioMimeType: string;
  lyricsSections: GenerateMusicLyricsSection[] | null;
  timestamps: GenerateMusicTimestamp[] | null;
  musicId: string | null;
  blobContainer: string;
  blobPath?: string;
  uploadStatus: UploadStatus;
  /** Incremented when equalizer settings are saved to blob. */
  equalizerRevision?: number;
  audio_blob_url?: string;
  metadataFileName?: string;
  stemsZipRef?: StemsZipRef;
  stemsZipName?: string;
  /** Prompt / guided selections restored from doc profile metadata. */
  editorRestore?: MusicEditorRestoreState;
  customData?: Record<string, any>;
}

export interface GenerateMusicCallbacks {
  onJobSubmitted?: () => void;
}

interface GenerateMusicRequest {
  prompt?: string;
  compositionPlan?: GenerateCompositionPlanResponse;
  musicLen?: number;
  forcedInstrumental: boolean;
  guidedSelections?: Record<string, string[]>;
  signal?: AbortSignal;
  callbacks?: GenerateMusicCallbacks;
}

function sectionsFromCompositionPlan(
  plan: GenerateCompositionPlanResponse,
): GenerateMusicLyricsSection[] {
  return plan.sections.map((section) => ({
    section_name: section.section_name,
    lines: sanitizeLyricLines(section.lines ?? []),
    duration_ms: section.duration_ms,
  }));
}

const generateMusic = async (
  request: GenerateMusicRequest,
): Promise<GenerateMusicResult> => {
  if (!request.prompt && !request.compositionPlan) {
    throw new Error("Either prompt or compositionPlan is required.");
  }

  const docEditorResult = await generateMusicViaDocEditor({
    prompt: request.prompt,
    compositionPlan: request.compositionPlan,
    musicLen: request.musicLen,
    forcedInstrumental: request.forcedInstrumental,
    guidedSelections: request.guidedSelections,
    signal: request.signal,
    onJobSubmitted: request.callbacks?.onJobSubmitted,
  });

  const lyricsSections =
    docEditorResult.lyricsSections ??
    (request.compositionPlan
      ? sectionsFromCompositionPlan(request.compositionPlan)
      : null);

  const timestamps = docEditorResult.timestamps;

  const audioBlobUrlLocal = URL.createObjectURL(docEditorResult.audioBlob);

  return {
    audioBlobUrl: audioBlobUrlLocal,
    audioBlob: docEditorResult.audioBlob,
    filename: docEditorResult.filename,
    audioMimeType: docEditorResult.streamMime,
    lyricsSections,
    timestamps,
    musicId: docEditorResult.musicId,
    blobContainer: docEditorResult.blobContainer,
    blobPath: docEditorResult.blobPath,
    uploadStatus: "uploaded",
    metadataFileName: docEditorResult.metadataFileName,
  };
};

export default generateMusic;
